/**
 * Versioned, immutable consent texts for EnrollmentCore.consent.
 * Source: docs/compliance/02_consent_wording.md (Witherspoon-CLO). STATUS: DRAFT,
 * not yet approved by outside counsel. Never edit a published version in place:
 * add a new version id. Ive-WBD may restyle but may not alter the text.
 *
 * The RENDERED string (supplier name filled in) is what gets recorded with the
 * consent event, together with the text_version_id. The server computes the sha256
 * and captures ip / user_agent (the browser cannot be trusted for those).
 */

// Legal documents are not published yet. Until CLO publishes them the labels render
// as plain text, not dead links. Set real paths here once the pages exist.
export const LEGAL_LINKS = { terms: null, privacy: null, providerPrivacy: null };

export const CONSENT_TEXT_VERSIONS = {
  tos: {
    id: "tos-2026-09-19-draft1",
    required: true,
    template: "I have read and agree to the RateCurrent {terms} and {privacy}.",
  },
  data_share: {
    id: "data_share-2026-09-19-draft1",
    required: true,
    // Omitted on purpose: the CLO draft sentence "RateCurrent will not share this
    // information with other energy providers for their marketing". CLO marked it
    // CONFIRM WITH OWNER; a promise we cannot keep is worse than silence.
    template:
      "I authorize {registered_name} (RateCurrent) to send the information I entered on this form to {provider}: my name, phone number, email, service address, selected plan, requested start date, and any additional information {provider} requires to process my enrollment. {provider} will use my information under its own {provider_privacy}. RateCurrent may be paid by {provider} if I enroll.",
  },
  sms: {
    id: "sms-2026-09-19-draft1",
    required: false,
    // Transactional/status only. Marketing-text language is deliberately NOT included
    // (CLO: ship SMS as transactional only, or not at all, until Texas registration status is resolved).
    template:
      "Yes, text me. I agree that {registered_name} (RateCurrent) may send text messages to the mobile number above, using an automated system, about my enrollment status and rate-expiration reminders. Message frequency varies. Message and data rates may apply. Consent is not a condition of enrollment or of any purchase. Reply STOP at any time to cancel or HELP for help. See our {terms} and {privacy}.",
  },
};

// Acknowledgments with no text-version id on the server (timestamps only). Wording: CLO 02_consent_wording.md Section 6.
export const ACK_TEXTS = {
  authority: "I am the person responsible for electric service at this address, or I am authorized to change the provider for this address.",
  supplier_docs: "I have reviewed the Electricity Facts Label, Terms of Service, and Your Rights as a Customer for this plan.",
};

/**
 * Returns { versionId, required, parts, plain }. `parts` lets the UI render links
 * where a URL exists and plain text otherwise; `plain` is the string recorded with
 * the consent event.
 */
export function renderConsent(type, { providerName, registeredName } = {}) {
  const def = CONSENT_TEXT_VERSIONS[type];
  const values = {
    provider: providerName || "the selected provider",
    // Stays neutral until the PUCT registration exists (CLO launch gate).
    registered_name: registeredName || "RateCurrent",
    terms: "Terms of Use",
    privacy: "Privacy Policy",
    provider_privacy: "privacy policy",
  };
  const linkFor = { terms: LEGAL_LINKS.terms, privacy: LEGAL_LINKS.privacy, provider_privacy: LEGAL_LINKS.providerPrivacy };

  const parts = [];
  def.template.split(/(\{[a-z_]+\})/).forEach((chunk) => {
    const m = chunk.match(/^\{([a-z_]+)\}$/);
    if (!m) {
      if (chunk) parts.push({ text: chunk });
      return;
    }
    const key = m[1];
    parts.push({ text: values[key], link: linkFor[key] || undefined, strong: key === "provider" });
  });
  return { versionId: def.id, required: def.required, parts, plain: parts.map((p) => p.text).join("") };
}
